import _once from 'lodash/once'; // eslint-disable-next-line no-restricted-imports

import { clearApplicationTranslationKeys } from '@tekion/tekion-i18n/utils';
import restClient from './apiService/apiService';
import apiCache, { API_CACHE_STORAGE_TYPE } from './apiService/apiCache';
import { getDefaultHeader } from './apiService/apiService.helper';
import { EMPTY_OBJECT } from '../app.constants';
import { removeValueFromLocalStorage } from '../utils/localStorage';
import { clearRecentlyUsedAppStorage } from '../utils/appSkeleton.utils';
import { clearDealerContext } from '../utils/auth';
import { removeCookie } from '../utils/cookieStorage';
import { clearApplicationLocalStorage } from '../utils/applicationLocalStorage.utils';
import TEnvReader from '../readers/Env';
var LOGIN_HEADERS = {
  clientId: 'web'
};
var PATHS = {
  LOGIN: '/login',
  OOB_CODE: '/mfa/binding_code',
  NEW_LOGIN: '/p/authenticate/password',
  MIMIC_LOGIN: '/p/authenticate/mimicking',
  SUPER_MIMIC_LOGIN: '/p/authenticate/mimicking/super-user',
  OTP_CODE: '/p/send-otp',
  RESEND_OTP: '/p/resend-otp',
  AUTHENTICATE_MFA: '/p/authenticate/mfa',
  NEW_LOGOUT: '/u/logout',
  GOOGLE_AUTHENICATOR: '/p/mfa/ga/qr-code',
  OKTA_AUTHENTICATOR: '/p/mfa/enroll-okta',
  ACTIVATE_OKTA: '/p/mfa/activate-okta',
  VALIDATE_EMAIL: '/p/identity-provider',
  IDENTITY_PROVIDER_DEALER: '/u/identity-provider',
  INTERNAL_USER_MIMIC_LOGIN: '/p/authenticate/mimicking/internal-user',
  MIMICED_USER_DETAILS: '/p/user-mimicking-details'
};
var loginUrl = TEnvReader.loginUrl(process.env);
var service = TEnvReader.service(process.env);
var newLoginService = TEnvReader.newLoginService(process.env);

var clearUserData = function clearUserData(shouldRedirect) {
  removeValueFromLocalStorage('t_token');
  removeValueFromLocalStorage('t_user');
  removeValueFromLocalStorage('dse_t_user');
  removeValueFromLocalStorage('mimickedDetails');
  removeValueFromLocalStorage('mimickingRequestStatus');
  removeValueFromLocalStorage('feedbackLastShownTime');
  removeValueFromLocalStorage('feedbackPersistStartTime');
  clearDealerContext();
  removeCookie('tcookie'); // This will never happen, but adding a check incase if user network issue
  // then deliberately removing the recently used app for the current user from local storage

  clearRecentlyUsedAppStorage();
  clearApplicationLocalStorage();
  clearApplicationTranslationKeys();
  apiCache.store.clear({
    storageTypesSet: new Set([API_CACHE_STORAGE_TYPE.IN_MEMORY_STORAGE, API_CACHE_STORAGE_TYPE.LOCAL_STORAGE])
  });
  if (!shouldRedirect) return;
  window.location.reload();
};

var newLogIn = function newLogIn(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.NEW_LOGIN), LOGIN_HEADERS).post('', payload).then(function (_ref) {
    var data = _ref.data;
    return data;
  });
};

var mimicLogIn = function mimicLogIn(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.MIMIC_LOGIN), LOGIN_HEADERS).post('', payload).then(function (_ref2) {
    var data = _ref2.data;
    return data;
  });
};

var superMimicLogin = function superMimicLogin(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.SUPER_MIMIC_LOGIN), LOGIN_HEADERS).post('', payload).then(function (_ref3) {
    var data = _ref3.data;
    return data;
  });
};

var getOOBCode = function getOOBCode(payload) {
  return restClient("".concat(loginUrl).concat(service).concat(PATHS.OOB_CODE), LOGIN_HEADERS).post('', payload).then(function (_ref4) {
    var data = _ref4.data;
    return data;
  });
};

var getOTP = function getOTP(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.OTP_CODE), LOGIN_HEADERS).post('', payload).then(function (_ref5) {
    var data = _ref5.data;
    return data;
  });
};

var resendOTP = function resendOTP(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.RESEND_OTP), LOGIN_HEADERS).post('', payload).then(function (_ref6) {
    var data = _ref6.data;
    return data;
  });
};

var verifyMFA = function verifyMFA(payload, dontChallengeMfa) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.AUTHENTICATE_MFA, "?dontChallengeMfa=").concat(dontChallengeMfa), LOGIN_HEADERS).post('', payload).then(function (_ref7) {
    var data = _ref7.data;
    return data;
  });
};

var newLogOut = function newLogOut() {
  var _ref8 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT,
      _ref8$shouldRedirect = _ref8.shouldRedirect,
      shouldRedirect = _ref8$shouldRedirect === void 0 ? true : _ref8$shouldRedirect;

  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.NEW_LOGOUT), getDefaultHeader()).get().then(function () {
    return clearUserData(shouldRedirect);
  })["catch"](function () {
    return clearUserData(shouldRedirect);
  });
};

var getGoogleAuthenticatorCode = function getGoogleAuthenticatorCode(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.GOOGLE_AUTHENICATOR), getDefaultHeader()).post('', payload).then(function (_ref9) {
    var data = _ref9.data;
    return data;
  });
};

var getOktaAuthenticatorCode = function getOktaAuthenticatorCode(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.OKTA_AUTHENTICATOR), getDefaultHeader()).post('', payload).then(function (_ref10) {
    var data = _ref10.data;
    return data;
  });
};

var activateOkta = function activateOkta(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.ACTIVATE_OKTA), getDefaultHeader()).post('', payload).then(function (_ref11) {
    var data = _ref11.data;
    return data;
  });
};

var validateEmail = function validateEmail(email) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.VALIDATE_EMAIL), getDefaultHeader()).post('', {
    email: email
  }).then(function (_ref12) {
    var data = _ref12.data;
    return data;
  });
};

var identityProviderDealer = function identityProviderDealer() {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.IDENTITY_PROVIDER_DEALER), getDefaultHeader()).get().then(function (_ref13) {
    var data = _ref13.data;
    return data;
  });
};

var internalUserMimicLogin = function internalUserMimicLogin(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.INTERNAL_USER_MIMIC_LOGIN), LOGIN_HEADERS).post('', payload).then(function (_ref14) {
    var data = _ref14.data;
    return data;
  });
};

var getMimickedUserDetails = function getMimickedUserDetails(payload) {
  return restClient("".concat(loginUrl).concat(newLoginService).concat(PATHS.MIMICED_USER_DETAILS), LOGIN_HEADERS).post('', payload).then(function (_ref15) {
    var data = _ref15.data;
    return data;
  });
};

export default {
  newLogIn: newLogIn,
  mimicLogIn: mimicLogIn,
  superMimicLogin: superMimicLogin,
  getOOBCode: getOOBCode,
  getOTP: getOTP,
  resendOTP: resendOTP,
  verifyMFA: verifyMFA,
  newLogOut: _once(newLogOut),
  getGoogleAuthenticatorCode: getGoogleAuthenticatorCode,
  getOktaAuthenticatorCode: getOktaAuthenticatorCode,
  activateOkta: activateOkta,
  validateEmail: validateEmail,
  identityProviderDealer: identityProviderDealer,
  internalUserMimicLogin: internalUserMimicLogin,
  getMimickedUserDetails: getMimickedUserDetails
};